<template>
  <div class="absolute top-0 left-0 z-50 w-screen h-screen">
    <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-85">
      <div class="z-10 w-screen h-full">
        <div class="h-full px-3 py-6 sm:p-10">
          <div
            class="relative grid grid-rows-[auto_1fr_auto] sm:grid-rows-[auto_1fr] w-full h-full overflow-y-auto sm:overflow-hidden bg-white rounded-lg shadow-xl">
            <div class="sm:sticky flex justify-between px-3 py-1.5 bg-cyan-light">
              <h1 class="text-sm font-medium text-theme-500">
                {{ $t('salesDocuments.documentPreview') }}
              </h1>
              <button type="button" @click="emit('close')">
                <Icon :icon="'@documents:ic:reduce'" class="w-4 h-4 ml-2 text-theme-700"></Icon>
              </button>
            </div>

            <div class="grid w-full max-h-[99%] h-full grid-rows-[auto_1fr_auto] px-3">
              <!-- Header -->
              <div class="py-2 sm:flex">
                <div class="sm:w-[65%] ml-1 flex flex-col">
                  <div class="flex flex-col">
                    <span class="text-sm font-medium">{{
                      modifiedSalesDocument?.customer?.trader?.name || '-'
                    }}</span>
                    <span class="text-xs">{{
                      modifiedSalesDocument?.customer?.trader?.address || '-'
                    }}</span>
                  </div>
                  <div class="flex flex-wrap w-full">
                    <div class="flex flex-col mt-2 sm:basis-1/4 basis-1/2 grow">
                      <span class="text-xs font-medium">{{
                        $t('salesDocuments.headers.deliveryDate')
                      }}</span>
                      <span class="text-xs font-light">{{
                        modifiedSalesDocument?.documentHeader.deliveryDate || '-'
                      }}</span>
                    </div>
                    <div class="flex flex-col mt-2 sm:basis-1/4 basis-1/2 grow">
                      <span class="text-xs font-medium">{{
                        $t('salesDocuments.headers.deliveryType')
                      }}</span>
                      <span class="text-xs font-light">{{
                        modifiedSalesDocument?.deliveryType?.name || '-'
                      }}</span>
                    </div>
                    <div class="flex flex-col mt-2 sm:basis-1/4 basis-1/2 grow">
                      <span class="text-xs font-medium">{{
                        $t('salesDocuments.headers.paymentType')
                      }}</span>
                      <span class="text-xs font-light">{{
                        modifiedSalesDocument?.documentHeader.documentPayment?.name || '-'
                      }}</span>
                    </div>
                    <div class="flex flex-col mt-2 sm:basis-1/4 basis-1/2 grow">
                      <span class="text-xs font-medium">
                        {{ $t('salesDocuments.headers.series') }}/{{
                          $t('salesDocuments.headers.number')
                        }}
                      </span>
                      <span class="text-xs font-light">
                        {{ modifiedSalesDocument?.documentHeader.documentSeries?.name }} #{{
                          modifiedSalesDocument?.documentHeader.documentNumber || '-'
                        }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="sm:w-[35%] flex flex-col gap-1">
                  <div
                    class="flex items-center justify-between h-full px-3 mt-2 rounded-md bg-cyan-light text-theme-500 min-h-7 sm:mt-0">
                    <span class="text-xs font-medium uppercase">
                      {{ $t('salesDocuments.totalNetValue') }}
                    </span>
                    <span
                      >{{
                        Number(modifiedSalesDocument?.documentHeader.totalNetValue).toFixed(2) ?? 0
                      }}
                      €</span
                    >
                  </div>
                  <div
                    class="flex items-center justify-between h-full px-3 rounded-md bg-cyan-light text-theme-500 min-h-7">
                    <span class="text-xs font-medium uppercase">
                      {{ $t('salesDocuments.totalQuantity') }}
                    </span>
                    <span>{{ modifiedSalesDocument?.documentHeader.totalQty }}</span>
                  </div>
                </div>
              </div>

              <!-- Details -->
              <div class="h-full sm:overflow-y-auto">
                <table class="w-full overflow-auto border-separate table-auto">
                  <tbody class="text-xs">
                    <tr
                      v-for="(documentDetail, index) in filteredDetails"
                      :key="index"
                      class="font-medium rounded-md shadow-sm even:bg-white odd:bg-slate-100">
                      <td class="p-2.5">
                        <div class="text-theme-500">{{ documentDetail.materialName }}</div>
                        <div class="font-light text-[10px] text-theme-500">
                          {{ documentDetail.materialCode }}
                        </div>
                        <div class="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-16">
                          <div class="grid w-full grid-cols-2 text-[10px] gap-16">
                            <div class="grid grid-cols-[5fr_2fr] grid-rows-3 w-full">
                              <span class="uppercase">{{ $t('salesDocuments.qty') }}</span>
                              <span class="text-right">{{ documentDetail.quantity || '-' }}</span>
                            </div>
                            <div class="grid grid-cols-[5fr_2fr] grid-rows-3 w-full">
                              <span class="uppercase">{{ $t('salesDocuments.unitPrice') }}</span>
                              <span class="text-right">{{
                                Number(documentDetail.unitPrice).toFixed(2) || '-'
                              }}</span>
                            </div>
                          </div>
                          <div class="text-[10px] grid grid-cols-2 sm:grid-cols-1 gap-10 sm:gap-0">
                            <div class="grid grid-cols-[5fr_2fr]">
                              <span class="uppercase">{{ $t('salesDocuments.discountPc') }}</span>
                              <span class="text-right">{{
                                documentDetail.percentageDiscount || '-'
                              }}</span>
                            </div>
                            <div class="grid grid-cols-[5fr_2fr]">
                              <span class="uppercase">{{
                                $t('salesDocuments.discountValue')
                              }}</span>
                              <span class="text-right">{{
                                Number(documentDetail.unitDiscount).toFixed(2) || '-'
                              }}</span>
                            </div>
                            <div class="grid grid-cols-[5fr_2fr]">
                              <span class="uppercase">{{ $t('salesDocuments.grossTotal') }}</span>
                              <span class="text-right">{{
                                Number(documentDetail.grossTotal).toFixed(2) || '-'
                              }}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Footer -->
              <div class="flex flex-col w-full gap-2 my-3 sm:flex-row">
                <div class="flex flex-col bg-gray-100 rounded-md sm:w-[70%] px-3 py-2">
                  <span class="text-xs font-medium capitalize">{{
                    $t('salesDocuments.comments')
                  }}</span>
                  <span class="text-sm">{{
                    modifiedSalesDocument?.documentHeader.remarks || ''
                  }}</span>
                </div>
                <div class="flex flex-col gap-1 sm:gap-0 sm:w-[30%]">
                  <div class="flex justify-between px-2">
                    <span class="text-xs font-medium capitalize">{{
                      $t('salesDocuments.totalDiscountValue')
                    }}</span>
                    <span class="text-xs"
                      >-{{
                        Number(modifiedSalesDocument?.documentHeader.totalDiscounts).toFixed(2) ?? 0
                      }}
                      €</span
                    >
                  </div>
                  <div class="flex justify-between px-2">
                    <span class="text-xs font-medium capitalize">{{
                      $t('salesDocuments.totalVat')
                    }}</span>
                    <span class="text-xs"
                      >{{
                        Number(modifiedSalesDocument?.documentHeader.totalVat).toFixed(2) ?? 0
                      }}
                      €</span
                    >
                  </div>
                  <div
                    class="flex items-center justify-between h-12 px-2 mt-2 rounded-md sm:h-8 grow bg-cyan-light text-theme-500">
                    <span class="text-sm font-medium uppercase">
                      {{ $t('salesDocuments.grossTotal') }}
                    </span>
                    <span
                      >{{
                        Number(modifiedSalesDocument?.documentHeader.totalGross).toFixed(2) ?? 0
                      }}
                      €</span
                    >
                  </div>
                </div>
              </div>
            </div>
            <div class="flex sm:hidden justify-between px-3 py-1.5 bg-cyan-light">
              <h1 class="text-sm font-medium text-theme-500">
                {{ $t('priceCollection.documentPreview') }}
              </h1>
              <button type="button" @click="emit('close')">
                <Icon :icon="'@documents:ic:reduce'" class="w-4 h-4 ml-2 text-theme-700"></Icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useSalesDocumentsStore } from '@/stores/salesDocuments';
import { Icon } from '@iconify/vue';

const { modifiedSalesDocument } = storeToRefs(useSalesDocumentsStore());
const emit = defineEmits(['close']);

const filteredDetails = computed(() => {
  return modifiedSalesDocument.value?.documentHeader.documentDetails?.filter((x) => !x.deleted);
});
</script>
